import React, { createContext, useContext, useReducer, useCallback, useEffect, useMemo, useRef } from "react";
import ConstraintsService from "../services/ConstraintsService";
import { useTimeZone } from "../../../../contexts/TimeZoneContext";
import dayjs from "dayjs";
import { ensureFlightTimeConsistency, calculateGroundTime } from "../../../../utils/timeUtils";
import { parsePositiveMinutes, syncRouteSnapshotBlockTime } from "../../../../utils/staBlockTimeAlignment";
import StaFollowBlockTimeService from "../services/StaFollowBlockTimeService";
import { message } from "antd";
import enhancedApi from "../../../../api/enhancedApi";
import { buildAircraftAvailabilityQuery } from "../../shared/aircraftAvailabilityConstraints";
import { useAircraftAvailabilityWindows } from "../../shared/useAircraftAvailabilityWindows";
import { pickUsableDefaultRoute } from "../../shared/routeBinding";
import { normalizeAirportCode } from "../../../../utils/airportCodeUtils";
import { getAirportMinGroundTime } from "../../../../utils/minGroundTimeUtils";

// 定义初始状态
const initialState = {
    schedule: {
        name: "",
        start_date: null,
        end_date: null,
        status: "DRAFT",
        aircraft_id: null,
    },
    parameters: {
        id: null,
        flight_number_prefix: "",
        min_ground_time: 60,
        transportation_demands: [],
        available_segments: [],
        maintenance_requirements: {},
        curfew_constraints: [],
        flight_compare_result: null,
    },
    flights: [],
    validationErrors: [],
    validationResults: [],
    isSaving: false,
    isDirty: false,
    batchAirportChangeLogs: [],
    constraints: {
        airportConstraints: {},
        loading: false,
        error: null,
        aircraftAvailability: {
            windowsByRegistration: {},
            registrations: [],
            queryStart: null,
            queryEnd: null,
            loading: false,
            fetching: false,
            error: null,
            updatedAt: null,
            stale: false,
        },
    },
    // 添加导入函数引用
    importFunction: null,
};

// 定义动作类型
const ActionTypes = {
    SET_SCHEDULE: "SET_SCHEDULE",
    UPDATE_SCHEDULE: "UPDATE_SCHEDULE",
    SET_FLIGHTS: "SET_FLIGHTS",
    ADD_FLIGHT: "ADD_FLIGHT",
    UPDATE_FLIGHT: "UPDATE_FLIGHT",
    REMOVE_FLIGHT: "REMOVE_FLIGHT",
    SET_PARAMETERS: "SET_PARAMETERS",
    UPDATE_PARAMETERS: "UPDATE_PARAMETERS",
    SET_VALIDATION_ERRORS: "SET_VALIDATION_ERRORS",
    SET_IS_SAVING: "SET_IS_SAVING",
    RESET_STATE: "RESET_STATE",
    // 约束相关动作类型
    SET_CONSTRAINTS_LOADING: "SET_CONSTRAINTS_LOADING",
    SET_AIRPORT_CONSTRAINTS: "SET_AIRPORT_CONSTRAINTS",
    SET_CONSTRAINTS_ERROR: "SET_CONSTRAINTS_ERROR",
    SET_AIRCRAFT_AVAILABILITY_STATE: "SET_AIRCRAFT_AVAILABILITY_STATE",
    SET_VALIDATION_RESULTS: "SET_VALIDATION_RESULTS",
    // 运输需求相关动作类型
    SET_TRANSPORTATION_DEMANDS: "SET_TRANSPORTATION_DEMANDS",
    UPDATE_TRANSPORTATION_DEMAND: "UPDATE_TRANSPORTATION_DEMAND",
    ADD_TRANSPORTATION_DEMAND: "ADD_TRANSPORTATION_DEMAND",
    REMOVE_TRANSPORTATION_DEMAND: "REMOVE_TRANSPORTATION_DEMAND",
    // 新增：单独的重置isDirty的动作类型
    RESET_DIRTY_STATUS: "RESET_DIRTY_STATUS",
    // 新增：更新比较结果的动作类型 - 不影响isDirty状态
    UPDATE_COMPARE_RESULT: "UPDATE_COMPARE_RESULT",
    SET_DIRTY: "SET_DIRTY",
    // 新增: 更新所有航班的飞机信息
    UPDATE_AIRCRAFT_IN_FLIGHTS: "UPDATE_AIRCRAFT_IN_FLIGHTS",
    // 新增: 设置导入函数
    SET_IMPORT_FUNCTION: "SET_IMPORT_FUNCTION",
    // 新增: 设置航班列表并标记为已修改
    SET_FLIGHTS_AND_MARK_DIRTY: "SET_FLIGHTS_AND_MARK_DIRTY",
    // 批量改机场：追加可追查备注
    APPEND_BATCH_AIRPORT_CHANGE_LOG: "APPEND_BATCH_AIRPORT_CHANGE_LOG",
};

// 定义Reducer
const scheduleEditorReducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.SET_SCHEDULE:
            return {
                ...state,
                schedule: action.payload,
                isDirty: false,
            };
        case ActionTypes.UPDATE_SCHEDULE:
            return {
                ...state,
                schedule: {
                    ...state.schedule,
                    ...action.payload,
                },
                isDirty: true,
            };
        case ActionTypes.SET_FLIGHTS:
            return {
                ...state,
                flights: action.payload,
                isDirty: false,
            };
        case ActionTypes.SET_FLIGHTS_AND_MARK_DIRTY:
            return {
                ...state,
                flights: action.payload,
                isDirty: true,
            };
        case ActionTypes.ADD_FLIGHT:
            return {
                ...state,
                flights: [...state.flights, action.payload],
                isDirty: true,
            };
        case ActionTypes.UPDATE_FLIGHT:
            return {
                ...state,
                flights: state.flights.map((flight) => (flight.id === action.payload.id ? { ...flight, ...action.payload } : flight)),
                isDirty: true,
            };
        case ActionTypes.REMOVE_FLIGHT:
            return {
                ...state,
                flights: state.flights.filter((flight) => flight.id !== action.payload),
                isDirty: true,
            };
        case ActionTypes.SET_PARAMETERS:
            return {
                ...state,
                parameters: action.payload,
                isDirty: false,
            };
        case ActionTypes.UPDATE_PARAMETERS:
            return {
                ...state,
                parameters: {
                    ...state.parameters,
                    ...action.payload,
                    // 确保transportation_demands不会被丢失，如果payload中没有提供，则保留原始值
                    transportation_demands: action.payload.transportation_demands !== undefined ? action.payload.transportation_demands : state.parameters.transportation_demands,
                    // 确保flight_compare_result不会被丢失
                    flight_compare_result: action.payload.flight_compare_result !== undefined ? action.payload.flight_compare_result : state.parameters.flight_compare_result,
                },
                isDirty: true,
            };
        case ActionTypes.SET_VALIDATION_ERRORS:
            return {
                ...state,
                validationErrors: action.payload,
            };
        case ActionTypes.SET_IS_SAVING:
            return {
                ...state,
                isSaving: action.payload,
            };
        case ActionTypes.RESET_STATE:
            return {
                ...initialState,
                schedule: {
                    ...initialState.schedule,
                    ...action.payload,
                },
            };
        // 约束相关Reducer处理
        case ActionTypes.SET_CONSTRAINTS_LOADING:
            return {
                ...state,
                constraints: {
                    ...state.constraints,
                    loading: action.payload,
                },
            };
        case ActionTypes.SET_AIRPORT_CONSTRAINTS:
            return {
                ...state,
                constraints: {
                    ...state.constraints,
                    airportConstraints:
                        typeof action.payload === "function"
                            ? action.payload(state.constraints.airportConstraints)
                            : action.payload,
                    loading: false,
                },
            };
        case ActionTypes.SET_CONSTRAINTS_ERROR:
            return {
                ...state,
                constraints: {
                    ...state.constraints,
                    error: action.payload,
                    loading: false,
                },
            };
        case ActionTypes.SET_AIRCRAFT_AVAILABILITY_STATE:
            return {
                ...state,
                constraints: {
                    ...state.constraints,
                    aircraftAvailability: {
                        ...state.constraints.aircraftAvailability,
                        ...action.payload,
                    },
                },
            };
        case ActionTypes.SET_VALIDATION_RESULTS:
            return {
                ...state,
                validationResults: action.payload,
            };
        // 运输需求相关Reducer处理
        case ActionTypes.SET_TRANSPORTATION_DEMANDS:
            return {
                ...state,
                parameters: {
                    ...state.parameters,
                    transportation_demands: action.payload,
                },
                isDirty: true,
            };
        case ActionTypes.ADD_TRANSPORTATION_DEMAND:
            return {
                ...state,
                parameters: {
                    ...state.parameters,
                    transportation_demands: [...state.parameters.transportation_demands, action.payload],
                },
                isDirty: true,
            };
        case ActionTypes.UPDATE_TRANSPORTATION_DEMAND:
            return {
                ...state,
                parameters: {
                    ...state.parameters,
                    transportation_demands: state.parameters.transportation_demands.map((demand) => (demand.id === action.payload.id ? { ...demand, ...action.payload } : demand)),
                },
                isDirty: true,
            };
        case ActionTypes.REMOVE_TRANSPORTATION_DEMAND:
            return {
                ...state,
                parameters: {
                    ...state.parameters,
                    transportation_demands: state.parameters.transportation_demands.filter((demand) => demand.id !== action.payload),
                },
                isDirty: true,
            };
        case ActionTypes.RESET_DIRTY_STATUS:
            return {
                ...state,
                isDirty: false,
                batchAirportChangeLogs: [],
            };
        // 处理比较结果的更新 - 不影响isDirty状态
        case ActionTypes.UPDATE_COMPARE_RESULT:
            return {
                ...state,
                parameters: {
                    ...state.parameters,
                    flight_compare_result: action.payload,
                },
                // 不修改isDirty状态
            };
        case ActionTypes.SET_DIRTY:
            return {
                ...state,
                isDirty: true,
            };
        // 处理更新所有航班的飞机信息
        case ActionTypes.UPDATE_AIRCRAFT_IN_FLIGHTS:
            return {
                ...state,
                flights: state.flights.map((flight) => ({
                    ...flight,
                    aircraft_reg: action.payload, // 将所有航班的飞机注册号更新为新值
                })),
                isDirty: true,
            };
        case ActionTypes.SET_IMPORT_FUNCTION:
            return {
                ...state,
                importFunction: action.payload,
            };
        case ActionTypes.APPEND_BATCH_AIRPORT_CHANGE_LOG:
            return {
                ...state,
                batchAirportChangeLogs: [
                    ...state.batchAirportChangeLogs,
                    action.payload,
                ],
            };
        default:
            return state;
    }
};

// 创建上下文
const ScheduleEditorContext = createContext();

// 创建Provider组件
export const ScheduleEditorProvider = ({ children, initialSchedule = null, onIsDirtyChange }) => {
    const [state, dispatch] = useReducer(scheduleEditorReducer, initialState);
    const { timeZone, airportsMap } = useTimeZone();
    const aircraftAvailabilityQuery = useMemo(
        () => buildAircraftAvailabilityQuery({
            flights: state.flights,
        }),
        [state.flights],
    );
    const aircraftAvailabilityQueryResult = useAircraftAvailabilityWindows(
        {
            registrations: aircraftAvailabilityQuery.registrations,
            start: aircraftAvailabilityQuery.start,
            end: aircraftAvailabilityQuery.end,
            status: "ACTIVE",
        },
        {
            enabled: Boolean(
                aircraftAvailabilityQuery.registrations.length
                && aircraftAvailabilityQuery.start
                && aircraftAvailabilityQuery.end
            ),
        },
    );

    // 使用ref来存储上一次验证的航班数据的标识（使用长度和第一个/最后一个航班ID）
    const lastValidatedFlights = useRef(null);
    // 机场约束（最小过站、宵禁、时区）同样是校验输入：运控在别的页面改了主数据
    // 切回来时航班列表没变，仅靠航班指纹会跳过校验，表格上的「过站配置缺失 /
    // 过站不足」标记就停在旧口径。约束内容变化也要触发重校验。
    const lastValidatedConstraints = useRef(null);

    // 当isDirty状态变化时调用onIsDirtyChange回调
    useEffect(() => {
        if (typeof onIsDirtyChange === "function") {
            onIsDirtyChange(state.isDirty);
        }
    }, [state.isDirty, onIsDirtyChange]);

    // 检查航班数据是否发生了实质性变化
    const hasFlightsChanged = useCallback((flights) => {
        if (!flights || !flights.length) {
            return lastValidatedFlights.current !== null;
        }

        // 创建当前航班数据的"指纹"
        const currentFingerprint = {
            length: flights.length,
            firstId: flights[0]?.id,
            lastId: flights[flights.length - 1]?.id,
            // 添加更严格的指纹 - 计算每个航班关键字段的哈希值
            itemsHash: flights
                .map((flight) => {
                    // 使用关键属性创建航班的唯一标识
                    const flightSignature = [flight.id, flight.flight_number, flight.aircraft_reg, flight.departure_airport, flight.arrival_airport, flight.std, flight.sta, flight.block_time_minutes, flight.ground_time_minutes, flight.flight_date].join("|");

                    // 简单哈希函数
                    let hash = 0;
                    for (let i = 0; i < flightSignature.length; i++) {
                        const char = flightSignature.charCodeAt(i);
                        hash = (hash << 5) - hash + char;
                        hash = hash & hash; // 转换为32位整数
                    }
                    return hash;
                })
                .join(","),
            timestamp: Date.now(),
        };

        // 如果没有上一次的数据，则返回true
        if (!lastValidatedFlights.current) {
            lastValidatedFlights.current = currentFingerprint;
            return true;
        }

        const prevFingerprint = lastValidatedFlights.current;

        // 检查是否有变化 - 包括长度、ID和详细的指纹
        const hasChanged = prevFingerprint.length !== currentFingerprint.length || prevFingerprint.itemsHash !== currentFingerprint.itemsHash;

        // 更新ref
        if (hasChanged) {
            lastValidatedFlights.current = currentFingerprint;
        } else {
            // 更新时间戳
            lastValidatedFlights.current = {
                ...prevFingerprint,
                timestamp: currentFingerprint.timestamp,
            };
        }

        return hasChanged;
    }, []);

    // 初始化状态
    React.useEffect(() => {
        if (initialSchedule) {
            dispatch({ type: ActionTypes.SET_SCHEDULE, payload: initialSchedule });

            if (initialSchedule.flights) {
                dispatch({ type: ActionTypes.SET_FLIGHTS, payload: initialSchedule.flights });
            }

            if (initialSchedule.parameters) {
                dispatch({ type: ActionTypes.SET_PARAMETERS, payload: initialSchedule.parameters });
            }
        }
    }, [initialSchedule]);

    // 辅助函数 - 自动校验航班约束
    const validateScheduleConstraints = useCallback(() => {
        const { flights, constraints, parameters } = state;

        if (!flights || flights.length === 0) {
            return;
        }

        // 检查航班数据是否有变化，避免重复验证；机场约束内容（最小过站 / 宵禁 /
        // 时区）变化同样要重校验，否则主数据改完切回来时校验标记不刷新
        const airportConstraints = constraints.airportConstraints || {};
        const constraintsFingerprint = JSON.stringify(
            Object.keys(airportConstraints)
                .sort()
                .map((code) => {
                    const item = airportConstraints[code] || {};
                    return [code, item.turnaround_time ?? null, item.timezone || null, item.dep_curfew_rules || [], item.arr_curfew_rules || []];
                }),
        );
        const constraintsChanged = lastValidatedConstraints.current !== constraintsFingerprint;

        if (!hasFlightsChanged(flights) && !constraintsChanged) {
            return;
        }
        lastValidatedConstraints.current = constraintsFingerprint;

        try {
            // 使用约束服务进行校验
            const results = ConstraintsService.validateSchedule(flights, constraints.airportConstraints, parameters, {
                timeZone,
                // 使用全局缓存的机场数据
                airportsMap: window.cachedAirports
                    ? window.cachedAirports.reduce((map, airport) => {
                          map[airport.iata_code] = airport;
                          return map;
                      }, {})
                    : {},
                aircraftAvailabilityWindowsByRegistration:
                    constraints?.aircraftAvailability?.windowsByRegistration || {},
            });

            dispatch({ type: ActionTypes.SET_VALIDATION_RESULTS, payload: results });
        } catch (error) {
            console.error("自动校验航班约束失败:", error);
        }
    }, [
        state.flights,
        state.constraints.airportConstraints,
        state.constraints.aircraftAvailability.windowsByRegistration,
        state.parameters,
        timeZone,
        hasFlightsChanged,
        dispatch,
    ]);

    // 添加防抖引用，防止频繁计算
    const validateDebounceTimerRef = useRef(null);

    // 创建一个防抖的验证函数
    const debouncedValidate = useCallback(() => {
        // 清除上一个定时器
        if (validateDebounceTimerRef.current) {
            clearTimeout(validateDebounceTimerRef.current);
        }

        // 设置新的定时器
        validateDebounceTimerRef.current = setTimeout(() => {
            validateScheduleConstraints();
        }, 500); // 增加防抖时间到500ms
    }, [validateScheduleConstraints]);

    // 航班数据发生变化时执行约束校验 - 优化依赖项
    useEffect(() => {
        // 使用防抖函数代替直接调用
        debouncedValidate();

        // 组件卸载时清除定时器
        return () => {
            if (validateDebounceTimerRef.current) {
                clearTimeout(validateDebounceTimerRef.current);
            }
        };
    }, [
        // 仅依赖航班数组长度和哈希值变化，减少不必要的重新验证
        state.flights.length,
        JSON.stringify(state.flights.map((f) => f.id)),
        debouncedValidate,
    ]);

    useEffect(() => {
        dispatch({
            type: ActionTypes.SET_AIRCRAFT_AVAILABILITY_STATE,
            payload: {
                registrations: aircraftAvailabilityQuery.registrations,
                queryStart: aircraftAvailabilityQuery.start,
                queryEnd: aircraftAvailabilityQuery.end,
                windowsByRegistration: aircraftAvailabilityQueryResult.error
                    ? state.constraints.aircraftAvailability.windowsByRegistration
                    : aircraftAvailabilityQueryResult.windowsByRegistration,
                loading: aircraftAvailabilityQueryResult.isLoading,
                fetching: aircraftAvailabilityQueryResult.isFetching,
                error: aircraftAvailabilityQueryResult.error || null,
                updatedAt: aircraftAvailabilityQueryResult.dataUpdatedAt || null,
                stale: Boolean(aircraftAvailabilityQueryResult.error),
            },
        });
        lastValidatedFlights.current = null;
        debouncedValidate();
        // 保留查询失败前的窗口数据，避免失败时把旧冲突清空。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        aircraftAvailabilityQuery.registrations.join(","),
        aircraftAvailabilityQuery.start,
        aircraftAvailabilityQuery.end,
        aircraftAvailabilityQueryResult.windowsByRegistration,
        aircraftAvailabilityQueryResult.isLoading,
        aircraftAvailabilityQueryResult.isFetching,
        aircraftAvailabilityQueryResult.error,
        aircraftAvailabilityQueryResult.dataUpdatedAt,
        state.flights.length,
        debouncedValidate,
    ]);

    // 操作方法
    const updateSchedule = useCallback((data) => {
        dispatch({ type: ActionTypes.UPDATE_SCHEDULE, payload: data });
    }, []);

    const updateParameters = useCallback((data) => {
        dispatch({ type: ActionTypes.UPDATE_PARAMETERS, payload: data });
    }, []);

    const setFlights = useCallback((flights) => {
        // 确保每个航班都有有效的display_order字段
        const processedFlights = flights.map((flight, index) => {
            // 如果display_order不存在或无效，则使用索引+1作为默认值
            const currentOrder = flight.display_order;
            const isValidOrder = currentOrder !== undefined && currentOrder !== null && !isNaN(Number(currentOrder)) && Number(currentOrder) > 0;

            // 确保时间字段格式一致 - 统一使用UTC标准格式
            const normalizedFlight = { ...flight };

            // 处理std格式 - 确保是UTC ISO格式
            if (normalizedFlight.std) {
                if (dayjs.isDayjs(normalizedFlight.std)) {
                    normalizedFlight.std = normalizedFlight.std.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
                } else if (typeof normalizedFlight.std === "string" && !normalizedFlight.std.endsWith("Z")) {
                    // 如果是字符串但不是UTC格式，转换为UTC格式
                    normalizedFlight.std = dayjs.utc(normalizedFlight.std).format("YYYY-MM-DDTHH:mm:ss") + "Z";
                }
                // flight_date 必须与std保持完全一致，包括日期和时间部分
                normalizedFlight.flight_date = normalizedFlight.std;
            }

            // 处理sta格式 - 确保是UTC ISO格式
            if (normalizedFlight.sta) {
                if (dayjs.isDayjs(normalizedFlight.sta)) {
                    normalizedFlight.sta = normalizedFlight.sta.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
                } else if (typeof normalizedFlight.sta === "string" && !normalizedFlight.sta.endsWith("Z")) {
                    // 如果是字符串但不是UTC格式，转换为UTC格式
                    normalizedFlight.sta = dayjs.utc(normalizedFlight.sta).format("YYYY-MM-DDTHH:mm:ss") + "Z";
                }
            }

            return {
                ...normalizedFlight,
                // 确保display_order是有效的数字
                display_order: isValidOrder ? Number(currentOrder) : index + 1,
            };
        });

        // 按照display_order排序
        const sortedFlights = [...processedFlights].sort((a, b) => {
            // 转换为数字进行比较
            const orderA = Number(a.display_order);
            const orderB = Number(b.display_order);

            // 确保即使转换后仍然无效也能比较（使用索引作为备选）
            if (isNaN(orderA) && isNaN(orderB)) return 0;
            if (isNaN(orderA)) return 1;
            if (isNaN(orderB)) return -1;

            return orderA - orderB;
        });

        // !!! 在 dispatch 前计算地面时间 !!!
        const flightsWithGroundTime = calculateAndSetActualGroundTimes(sortedFlights);

        dispatch({ type: ActionTypes.SET_FLIGHTS_AND_MARK_DIRTY, payload: flightsWithGroundTime });

        // 手动设置lastValidatedFlights为null，强制下次进行验证
        lastValidatedFlights.current = null;
    }, []);

    const addFlight = useCallback(
        (flight) => {
            // 标准化时间格式处理
            const normalizedFlight = { ...flight };

            // 处理std格式 - 确保是UTC ISO格式
            if (normalizedFlight.std) {
                if (dayjs.isDayjs(normalizedFlight.std)) {
                    normalizedFlight.std = normalizedFlight.std.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
                } else if (typeof normalizedFlight.std === "string" && !normalizedFlight.std.endsWith("Z")) {
                    // 如果是字符串但不是UTC格式，转换为UTC格式
                    normalizedFlight.std = dayjs.utc(normalizedFlight.std).format("YYYY-MM-DDTHH:mm:ss") + "Z";
                }
                // flight_date必须与std保持完全一致
                normalizedFlight.flight_date = normalizedFlight.std;
            }

            // 处理sta格式 - 确保是UTC ISO格式
            if (normalizedFlight.sta) {
                if (dayjs.isDayjs(normalizedFlight.sta)) {
                    normalizedFlight.sta = normalizedFlight.sta.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
                } else if (typeof normalizedFlight.sta === "string" && !normalizedFlight.sta.endsWith("Z")) {
                    // 如果是字符串但不是UTC格式，转换为UTC格式
                    normalizedFlight.sta = dayjs.utc(normalizedFlight.sta).format("YYYY-MM-DDTHH:mm:ss") + "Z";
                }
            }

            // 确保时间一致性
            const timeConsistentFlight = ensureFlightTimeConsistency(normalizedFlight, "UTC");

            // 构造下一个状态的 flights 数组
            const nextFlightsState = [...state.flights, timeConsistentFlight];

            // !!! 计算地面时间 !!!
            const flightsWithGroundTime = calculateAndSetActualGroundTimes(nextFlightsState);

            // 使用 SET_FLIGHTS action 更新整个列表
            dispatch({ type: ActionTypes.SET_FLIGHTS_AND_MARK_DIRTY, payload: flightsWithGroundTime });

            // 不要立即强制验证，让防抖机制来处理
            if (timeConsistentFlight.isMassUpdate) {
                lastValidatedFlights.current = null;
            }
        },
        [state.flights, debouncedValidate],
    );

    const updateFlight = useCallback(
        (flight) => {
            // 标准化时间格式处理
            const normalizedFlight = { ...flight };

            // 处理std格式 - 确保是UTC ISO格式
            if (normalizedFlight.std) {
                if (dayjs.isDayjs(normalizedFlight.std)) {
                    normalizedFlight.std = normalizedFlight.std.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
                } else if (typeof normalizedFlight.std === "string" && !normalizedFlight.std.endsWith("Z")) {
                    // 如果是字符串但不是UTC格式，转换为UTC格式
                    normalizedFlight.std = dayjs.utc(normalizedFlight.std).format("YYYY-MM-DDTHH:mm:ss") + "Z";
                }
                // flight_date必须与std保持完全一致
                normalizedFlight.flight_date = normalizedFlight.std;
            }

            // 处理sta格式 - 确保是UTC ISO格式
            if (normalizedFlight.sta) {
                if (dayjs.isDayjs(normalizedFlight.sta)) {
                    normalizedFlight.sta = normalizedFlight.sta.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
                } else if (typeof normalizedFlight.sta === "string" && !normalizedFlight.sta.endsWith("Z")) {
                    // 如果是字符串但不是UTC格式，转换为UTC格式
                    normalizedFlight.sta = dayjs.utc(normalizedFlight.sta).format("YYYY-MM-DDTHH:mm:ss") + "Z";
                }
            }

            // 确保时间一致性 - 使用timeUtils中的ensureFlightTimeConsistency函数
            const timeConsistentFlight = ensureFlightTimeConsistency(normalizedFlight, "UTC");

            // 构造下一个状态的 flights 数组
            const nextFlightsState = state.flights.map((f) => (f.id === timeConsistentFlight.id ? { ...f, ...timeConsistentFlight } : f));

            // !!! 计算地面时间 !!!
            const flightsWithGroundTime = calculateAndSetActualGroundTimes(nextFlightsState);

            // 使用 SET_FLIGHTS action 更新整个列表
            dispatch({ type: ActionTypes.SET_FLIGHTS_AND_MARK_DIRTY, payload: flightsWithGroundTime });

            // 触发验证（如果需要）
            const isTimeFieldChange = flight.std !== normalizedFlight.std || flight.sta !== normalizedFlight.sta || flight.flight_date !== normalizedFlight.flight_date;
            if (isTimeFieldChange || flight.isMassUpdate) {
                debouncedValidate();
            }
        },
        [state.flights, debouncedValidate], // 添加 state.flights 依赖
    );

    const removeFlight = useCallback(
        (flightId) => {
            // 构造下一个状态的 flights 数组
            const nextFlightsState = state.flights.filter((flight) => flight.id !== flightId);

            // !!! 计算地面时间 !!!
            const flightsWithGroundTime = calculateAndSetActualGroundTimes(nextFlightsState);

            // 使用 SET_FLIGHTS action 更新整个列表
            dispatch({ type: ActionTypes.SET_FLIGHTS_AND_MARK_DIRTY, payload: flightsWithGroundTime });

            // 删除操作通常是大的结构变化，应该触发重新验证
            debouncedValidate();
        },
        [state.flights, debouncedValidate],
    );

    // 添加批量操作函数，用于优化批量添加/更新航班的性能
    // options.silent: 如果为 true，则使用 SET_FLIGHTS（不设置 isDirty），用于派生数据的自动重算
    const batchUpdateFlights = useCallback(
        (updatedFlights, options = {}) => {
            const { silent = false } = options;

            if (!updatedFlights || !Array.isArray(updatedFlights) || updatedFlights.length === 0) {
                return;
            }

            // 处理所有航班时间格式
            const normalizedFlights = updatedFlights.map((flight) => {
                const normalized = { ...flight };

                // 处理std格式
                if (normalized.std) {
                    if (dayjs.isDayjs(normalized.std)) {
                        normalized.std = normalized.std.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
                    } else if (typeof normalized.std === "string" && !normalized.std.endsWith("Z")) {
                        normalized.std = dayjs.utc(normalized.std).format("YYYY-MM-DDTHH:mm:ss") + "Z";
                    }
                    // flight_date必须与std保持完全一致
                    normalized.flight_date = normalized.std;
                }

                // 处理sta格式
                if (normalized.sta) {
                    if (dayjs.isDayjs(normalized.sta)) {
                        normalized.sta = normalized.sta.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
                    } else if (typeof normalized.sta === "string" && !normalized.sta.endsWith("Z")) {
                        normalized.sta = dayjs.utc(normalized.sta).format("YYYY-MM-DDTHH:mm:ss") + "Z";
                    }
                }

                // 确保时间一致性
                return ensureFlightTimeConsistency(normalized, "UTC");
            });

            // 按照display_order排序
            const sortedFlights = [...normalizedFlights].sort((a, b) => {
                const orderA = Number(a.display_order);
                const orderB = Number(b.display_order);
                if (isNaN(orderA) && isNaN(orderB)) return 0;
                if (isNaN(orderA)) return 1;
                if (isNaN(orderB)) return -1;
                return orderA - orderB;
            });

            // !!! 在 dispatch 前计算地面时间 !!!
            const flightsWithGroundTime = calculateAndSetActualGroundTimes(sortedFlights);

            // 批量更新航班
            // silent 模式用于派生数据的自动重算（如轮换信息），不应触发 isDirty
            if (silent) {
                dispatch({ type: ActionTypes.SET_FLIGHTS, payload: flightsWithGroundTime });
            } else {
                dispatch({ type: ActionTypes.SET_FLIGHTS_AND_MARK_DIRTY, payload: flightsWithGroundTime });
            }

            // 批量更新一定要触发重新验证
            lastValidatedFlights.current = null;
            debouncedValidate();
        },
        [debouncedValidate],
    );

    const setValidationErrors = useCallback((errors) => {
        dispatch({ type: ActionTypes.SET_VALIDATION_ERRORS, payload: errors });
    }, []);

    const setIsSaving = useCallback((isSaving) => {
        dispatch({ type: ActionTypes.SET_IS_SAVING, payload: isSaving });
    }, []);

    const resetState = useCallback((initialData = {}) => {
        dispatch({ type: ActionTypes.RESET_STATE, payload: initialData });
    }, []);

    // 约束相关方法
    const setConstraintsLoading = useCallback((loading) => {
        dispatch({ type: ActionTypes.SET_CONSTRAINTS_LOADING, payload: loading });
    }, []);

    const setAirportConstraints = useCallback((constraints) => {
        dispatch({ type: ActionTypes.SET_AIRPORT_CONSTRAINTS, payload: constraints });
    }, []);

    const setConstraintsError = useCallback((error) => {
        dispatch({ type: ActionTypes.SET_CONSTRAINTS_ERROR, payload: error });
    }, []);

    const refreshAircraftAvailability = useCallback(async () => {
        if (!aircraftAvailabilityQuery.registrations.length) {
            return {};
        }
        const result = await aircraftAvailabilityQueryResult.refetch();
        if (result.error) {
            throw result.error;
        }
        return result.data || {};
    }, [
        aircraftAvailabilityQuery.registrations.length,
        aircraftAvailabilityQueryResult,
    ]);

    const setValidationResults = useCallback((results) => {
        dispatch({ type: ActionTypes.SET_VALIDATION_RESULTS, payload: results });
    }, []);

    // 运输需求相关方法
    const setTransportationDemands = useCallback((demands) => {
        dispatch({ type: ActionTypes.SET_TRANSPORTATION_DEMANDS, payload: demands });
    }, []);

    const addTransportationDemand = useCallback((demand) => {
        dispatch({ type: ActionTypes.ADD_TRANSPORTATION_DEMAND, payload: demand });
    }, []);

    const updateTransportationDemand = useCallback((demand) => {
        dispatch({ type: ActionTypes.UPDATE_TRANSPORTATION_DEMAND, payload: demand });
    }, []);

    const removeTransportationDemand = useCallback((demandId) => {
        dispatch({ type: ActionTypes.REMOVE_TRANSPORTATION_DEMAND, payload: demandId });
    }, []);

    // 新增：重置isDirty状态的方法
    const resetDirtyStatus = useCallback(() => {
        dispatch({ type: ActionTypes.RESET_DIRTY_STATUS });
    }, []);

    const refreshMasterDataAndUpdateFlights = useCallback(async () => {
        if (!state.flights || state.flights.length === 0) {
            message.info("航班列表为空，无需刷新。");
            return;
        }

        message.loading({ content: "正在刷新基础数据...", key: "refreshData", duration: 0 });
        const routeIds = Array.from(new Set(state.flights.map((flight) => flight.flight_route_id).filter(Boolean)));
        const aircraftReg = state.schedule?.aircraft?.registration;
        // 逐班判断「这架飞机能用的默认」：有可用默认就改绑默认，没有就不动绑定（不退到飞机专用）
        const legPairs = [];
        const seenLegKeys = new Set();
        state.flights.forEach((flight) => {
            if (!flight?.departure_airport || !flight?.arrival_airport) return;
            const legKey = `${flight.departure_airport}-${flight.arrival_airport}`;
            if (seenLegKeys.has(legKey)) return;
            seenLegKeys.add(legKey);
            legPairs.push({ legKey, departureAirport: flight.departure_airport, arrivalAirport: flight.arrival_airport });
        });
        const defaultRouteLookup = (async () => {
            const routesByLeg = new Map();
            const failedLegs = [];
            const legResults = await Promise.all(
                legPairs.map(async ({ legKey, departureAirport, arrivalAirport }) => {
                    try {
                        const response = await enhancedApi.flightRoute.getRoutesByLeg(departureAirport, arrivalAirport, true);
                        return { legKey, defaultRoute: pickUsableDefaultRoute(response?.data || [], aircraftReg) };
                    } catch (error) {
                        console.error(`刷新基础数据时读取 ${legKey} 的航路主数据失败:`, error);
                        return { legKey, failed: true };
                    }
                }),
            );
            legResults.forEach((result) => {
                if (result.failed) {
                    failedLegs.push(result.legKey);
                    return;
                }
                if (result.defaultRoute) {
                    routesByLeg.set(result.legKey, result.defaultRoute);
                }
            });
            return { routesByLeg, failedLegs };
        })();
        const [constraintsResult, routesResult, availabilityResult, defaultRouteResult] = await Promise.allSettled([
            enhancedApi.masterData.airport.getAllAirportConstraints(),
            routeIds.length > 0 ? enhancedApi.flightRoute.getByIds(routeIds) : Promise.resolve(null),
            refreshAircraftAvailability(),
            defaultRouteLookup,
        ]);

        let newAirportConstraintsMap = null;
        if (constraintsResult.status === "fulfilled") {
            const allConstraints = constraintsResult.value?.data || [];
            newAirportConstraintsMap = allConstraints.reduce((acc, constraint) => {
                const code = normalizeAirportCode(constraint.iata_code);
                if (code) {
                    constraint.dep_curfew_rules = ConstraintsService.convertCurfewRuleFields(constraint.dep_curfew_rules || []);
                    constraint.arr_curfew_rules = ConstraintsService.convertCurfewRuleFields(constraint.arr_curfew_rules || []);
                    acc[code] = constraint;
                }
                return acc;
            }, {});
            setAirportConstraints(newAirportConstraintsMap);
        }

        const routesMap = routesResult.status === "fulfilled"
            ? routesResult.value?.data || {}
            : null;
        const defaultRoutesMap = defaultRouteResult.status === "fulfilled"
            ? defaultRouteResult.value.routesByLeg
            : new Map();
        const defaultRouteFailures = defaultRouteResult.status === "fulfilled"
            ? defaultRouteResult.value.failedLegs
            : legPairs.map(({ legKey }) => legKey);
        let reboundToDefaultCount = 0;
        const defaultRoutesWithoutBlockTime = new Set();
        if (newAirportConstraintsMap || routesMap || defaultRoutesMap.size > 0) {
            const sourceFlights = state.flights;
            const updatedFlights = sourceFlights.map((flight) => {
                let newFlight = { ...flight };
                let isModified = false;
                const usableDefaultRoute = defaultRoutesMap.get(`${flight.departure_airport}-${flight.arrival_airport}`);
                const defaultBlockTimeMinutes = parsePositiveMinutes(usableDefaultRoute?.block_time_minutes);
                if (usableDefaultRoute && String(usableDefaultRoute.id) !== String(flight.flight_route_id || "")) {
                    // 有这架飞机能用的默认：已绑别的改绑、没绑的补绑（人手选的例外同样刷回），
                    // 轮档与到达跟着默认走；仍生效的手填到达由 applyRouteBlockTimeStaPolicy 保护。
                    // 快照里的航路 id / 名称不在这里同步：保存时后端按 flight_route_id 重建快照，
                    // 会话内读它的地方只读轮档分钟（下面会一起处理）。
                    const reboundFlight = StaFollowBlockTimeService.applyRouteBlockTimeStaPolicy(
                        {
                            ...newFlight,
                            flight_route_id: usableDefaultRoute.id,
                            flight_route_name: usableDefaultRoute.route_name || null,
                        },
                        defaultBlockTimeMinutes,
                    );
                    if (defaultBlockTimeMinutes == null) {
                        // 默认航路没有有效轮档：不沿用旧航路上的数字，也不发明数字。
                        // 轮档字段（含快照里的）留空并报给运控，等主数据补上再刷新。
                        defaultRoutesWithoutBlockTime.add(`${flight.departure_airport}-${flight.arrival_airport}`);
                        newFlight = syncRouteSnapshotBlockTime(reboundFlight, null);
                    } else {
                        newFlight = reboundFlight;
                    }
                    isModified = true;
                    reboundToDefaultCount += 1;
                } else if (routesMap) {
                    const route = flight.flight_route_id ? routesMap[flight.flight_route_id] : null;
                    const normalizedBlockTime = parsePositiveMinutes(route?.block_time_minutes);
                    if (flight.flight_route_id) {
                        const withRouteName = {
                            ...newFlight,
                            flight_route_name: route?.route_name || newFlight.flight_route_name || null,
                        };
                        const nextFlight = StaFollowBlockTimeService.applyMasterRouteBlockTimeRefresh(withRouteName, normalizedBlockTime);
                        const changed =
                            nextFlight.sta !== newFlight.sta ||
                            nextFlight.block_time_minutes !== newFlight.block_time_minutes ||
                            nextFlight.min_block_time_minutes !== newFlight.min_block_time_minutes ||
                            nextFlight.flight_route_block_time_minutes !== newFlight.flight_route_block_time_minutes ||
                            nextFlight.flight_route_name !== newFlight.flight_route_name ||
                            Boolean(newFlight.additional_info?.sta_manual) !== Boolean(nextFlight.additional_info?.sta_manual);
                        if (changed) {
                            newFlight = nextFlight;
                            isModified = true;
                        }
                    } else {
                        const clearedRouteFields = {
                            flight_route_name: null,
                            flight_route_block_time_minutes: null,
                            min_block_time_minutes: null,
                            block_time_minutes: null,
                        };
                        Object.entries(clearedRouteFields).forEach(([field, value]) => {
                            if (newFlight[field] !== value) {
                                isModified = true;
                                newFlight[field] = value;
                            }
                        });
                    }
                }
                // 按机场当前值改写航班上存着的最小过站：机场当前为空就写成空（不是 60），
                // 0 保持 0。自动填时刻不依赖这次改写是否发生过
                if (newAirportConstraintsMap) {
                    const newMinGroundTime = getAirportMinGroundTime(newAirportConstraintsMap, flight.arrival_airport);
                    if (newMinGroundTime !== (newFlight.min_ground_time_minutes ?? null)) {
                        isModified = true;
                        newFlight.min_ground_time_minutes = newMinGroundTime;
                    }
                }
                return isModified ? ensureFlightTimeConsistency(newFlight) : newFlight;
            });
            batchUpdateFlights(updatedFlights);
            const staChanges = StaFollowBlockTimeService.collectStaRewrites(sourceFlights, updatedFlights);
            const refreshChangeNotice = StaFollowBlockTimeService.formatRefreshRebindNotice(reboundToDefaultCount, staChanges, { timeZone, airportsMap });
            if (refreshChangeNotice) {
                message.info(refreshChangeNotice, 8);
            }
        }

        if (defaultRoutesWithoutBlockTime.size > 0) {
            message.warning(`以下起降的默认航路没有有效轮档，相关航班已改绑到默认但轮档留空，请先到航路主数据补轮档后再刷新：${Array.from(defaultRoutesWithoutBlockTime).join("、")}`, 8);
        }

        const failures = [
            constraintsResult.status === "rejected" ? "机场约束" : null,
            routesResult.status === "rejected" ? "航路" : null,
            availabilityResult.status === "rejected" ? "飞机可用性" : null,
            defaultRouteFailures.length > 0 ? `默认航路（${defaultRouteFailures.join("、")}，这些航段未判断是否该改绑）` : null,
        ].filter(Boolean);
        if (failures.length === 0) {
            message.success({ content: "基础数据刷新完毕！", key: "refreshData", duration: 2 });
        } else {
            message.warning({
                content: `部分基础数据刷新失败：${failures.join("、")}。已保留失败部分的旧数据并标记为可能过期。`,
                key: "refreshData",
                duration: 4,
            });
        }
    }, [
        state.flights,
        state.schedule,
        batchUpdateFlights,
        setAirportConstraints,
        refreshAircraftAvailability,
        timeZone,
        airportsMap,
    ]);

    // 提供上下文值
    const contextValue = {
        state,
        updateSchedule,
        updateParameters,
        setFlights,
        addFlight,
        updateFlight,
        removeFlight,
        batchUpdateFlights,
        setValidationErrors,
        setIsSaving,
        resetState,
        resetDirtyStatus,
        setConstraintsLoading,
        setAirportConstraints,
        setConstraintsError,
        refreshAircraftAvailability,
        setValidationResults,
        validateScheduleConstraints,
        setTransportationDemands,
        addTransportationDemand,
        updateTransportationDemand,
        removeTransportationDemand,
        updateCompareResult: useCallback((result) => {
            dispatch({ type: ActionTypes.UPDATE_COMPARE_RESULT, payload: result });
        }, []),
        setDirty: useCallback(() => {
            dispatch({ type: ActionTypes.SET_DIRTY });
        }, []),
        // 添加新方法: 更新所有航班的飞机
        updateAircraftInFlights: useCallback((aircraftReg) => {
            dispatch({ type: ActionTypes.UPDATE_AIRCRAFT_IN_FLIGHTS, payload: aircraftReg });
        }, []),
        // 添加新方法: 设置导入函数
        setImportFunction: useCallback((func) => {
            dispatch({ type: ActionTypes.SET_IMPORT_FUNCTION, payload: func });
        }, []),
        appendBatchAirportChangeLog: useCallback((log) => {
            dispatch({ type: ActionTypes.APPEND_BATCH_AIRPORT_CHANGE_LOG, payload: log });
        }, []),
        refreshMasterDataAndUpdateFlights,
    };

    return <ScheduleEditorContext.Provider value={contextValue}>{children}</ScheduleEditorContext.Provider>;
};

// 自定义Hook，便于消费上下文
export const useScheduleEditor = () => {
    const context = useContext(ScheduleEditorContext);
    if (!context) {
        throw new Error("useScheduleEditor must be used within a ScheduleEditorProvider");
    }
    return context;
};

export { ActionTypes };

// 核心计算函数: 计算并设置实际地面时间
const calculateAndSetActualGroundTimes = (flights) => {
    if (!flights || flights.length === 0) {
        return flights; // 如果没有航班，直接返回
    }
    if (flights.length === 1) {
        // 如果只有一个航班，地面时间必为 null
        return [{ ...flights[0], ground_time_minutes: null }];
    }

    // 1. 按照飞机注册号和起飞时间排序
    const sortedFlights = [...flights].sort((a, b) => {
        if (a.aircraft_reg !== b.aircraft_reg) {
            return (a.aircraft_reg || "").localeCompare(b.aircraft_reg || "");
        }
        const stdA = dayjs.utc(a.std);
        const stdB = dayjs.utc(b.std);
        if (!stdA.isValid() && !stdB.isValid()) return 0;
        if (!stdA.isValid()) return 1;
        if (!stdB.isValid()) return -1;
        return stdA.valueOf() - stdB.valueOf();
    });

    // 2. 使用 Map 来存储更新后的航班，以便后面恢复原始顺序
    const updatedFlightsMap = new Map();
    // 先将所有航班放入 Map，并将 ground_time_minutes 初始化为 null
    flights.forEach((flight) => {
        updatedFlightsMap.set(flight.id, { ...flight, ground_time_minutes: null });
    });

    // 3. 遍历排序后的列表，计算并更新 *前一个* 航班的地面时间
    for (let i = 1; i < sortedFlights.length; i++) {
        const currentFlight = sortedFlights[i];
        const prevFlight = sortedFlights[i - 1];

        // 检查是否是同一架飞机且时间有效
        if (prevFlight.aircraft_reg === currentFlight.aircraft_reg && prevFlight.sta && dayjs.utc(prevFlight.sta).isValid() && currentFlight.std && dayjs.utc(currentFlight.std).isValid()) {
            // 计算 prevFlight 降落后，currentFlight 起飞前的地面时间
            const actualGroundTime = calculateGroundTime(prevFlight.sta, currentFlight.std);

            // 获取 Map 中对应的 prevFlight 的副本进行更新
            const updatedPrevFlight = updatedFlightsMap.get(prevFlight.id);
            if (updatedPrevFlight) {
                updatedPrevFlight.ground_time_minutes = actualGroundTime;
            }
        }
    }

    // 4. 从 Map 中提取更新后的航班列表，保持原始输入顺序
    return flights.map((originalFlight) => updatedFlightsMap.get(originalFlight.id));
};
